import { Component, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import mermaid from 'mermaid';

@Component({
    selector: 'shared-org-structure-flow-dialog',
    imports: [CommonModule, MatDialogModule, MatIconModule, MatButtonModule, MatTooltipModule],
    template: `
        <div class="dialog-container">
            <!-- Header -->
            <div class="dialog-header">
                <div class="header-left">
                    <mat-icon class="header-icon">account_tree</mat-icon>
                    <h2>How the Organization Structure Works</h2>
                </div>
                <div class="header-actions">
                    <button
                        mat-icon-button
                        (click)="activeTab = 'explanation'"
                        [class.active]="activeTab === 'explanation'"
                        matTooltip="Guide: how the organisation structure works"
                    >
                        <mat-icon>article</mat-icon>
                    </button>
                    <button
                        mat-icon-button
                        (click)="switchToDiagram()"
                        [class.active]="activeTab === 'diagram'"
                        matTooltip="Visual flow diagram"
                    >
                        <mat-icon>account_tree</mat-icon>
                    </button>
                    <button
                        mat-icon-button
                        (click)="switchToOntology()"
                        [class.active]="activeTab === 'ontology'"
                        matTooltip="Entity ontology"
                    >
                        <mat-icon>hub</mat-icon>
                    </button>
                    <button mat-icon-button (click)="close()" matTooltip="Close">
                        <mat-icon>close</mat-icon>
                    </button>
                </div>
            </div>

            <!-- Explanation Tab -->
            <div class="content explanation-content" [class.tab-hidden]="activeTab !== 'explanation'">
                <!-- The guide itself is docs/categorized/guides/ORG-STRUCTURE-GUIDE.html, copied in as an app asset -->
                <iframe class="guide-frame" [src]="guideUrl" title="How the organisation structure works"></iframe>
            </div>

            <!-- Ontology Tab -->
            <div class="content ontology-content" [class.tab-hidden]="activeTab !== 'ontology'">
                <div class="ontology-sections">
                    <div class="ontology-intro">
                        <h3><mat-icon>hub</mat-icon> Entity Ontology</h3>
                        <p>
                            This shows every entity in the system and how they connect. The <strong>Hat</strong> is the
                            central hub linking positions, users, security, and policy.
                        </p>
                    </div>

                    <!-- Entity cards -->
                    <div class="entity-grid">
                        <div class="entity-card org-card">
                            <div class="entity-header"><mat-icon>apartment</mat-icon> Organization</div>
                            <div class="entity-body">
                                <p>Top-level tenant. Contains all other entities.</p>
                                <div class="entity-fields">
                                    <span class="field"><strong>key</strong>: string</span>
                                    <span class="field"><strong>name</strong>: string</span>
                                    <span class="field"><strong>orgParts</strong>: OrgPart[]</span>
                                    <span class="field"><strong>orgHats</strong>: map</span>
                                    <span class="field"><strong>orgSecurityGroups</strong>: map</span>
                                    <span class="field"><strong>orgFeatures</strong>: map</span>
                                    <span class="field"><strong>orgPolicies</strong>: map</span>
                                    <span class="field"><strong>orgUsers</strong>: map</span>
                                </div>
                            </div>
                        </div>

                        <div class="entity-card position-card">
                            <div class="entity-header"><mat-icon>work</mat-icon> Position (OrgPart)</div>
                            <div class="entity-body">
                                <p>A node in the org tree. Self-referential hierarchy via parentPartKey.</p>
                                <div class="entity-fields">
                                    <span class="field"><strong>partKey</strong>: string</span>
                                    <span class="field"><strong>name</strong>: string</span>
                                    <span class="field"><strong>partType</strong>: Executive&hellip;Post</span>
                                    <span class="field"><strong>depth</strong>: 0&ndash;5</span>
                                    <span class="field"><strong>parentPartKey</strong> &rarr; OrgPart</span>
                                    <span class="field"><strong>path</strong>: children.N.data</span>
                                </div>
                            </div>
                        </div>

                        <div class="entity-card hat-card">
                            <div class="entity-header"><mat-icon>school</mat-icon> Hat &mdash; Central Hub</div>
                            <div class="entity-body">
                                <p>The linking entity. Connects positions, users, security groups, and policies.</p>
                                <div class="entity-fields">
                                    <span class="field"><strong>key</strong>: string</span>
                                    <span class="field"><strong>name</strong>: string</span>
                                    <span class="field rel"><strong>positionArray</strong> &rarr; OrgPart[]</span>
                                    <span class="field rel"><strong>assignedToArray</strong> &rarr; User[]</span>
                                    <span class="field rel"><strong>securityGroupsArray</strong> &rarr; SG[]</span>
                                    <span class="field rel"><strong>policyKeys</strong> &rarr; Policy[]</span>
                                </div>
                            </div>
                        </div>

                        <div class="entity-card sg-card">
                            <div class="entity-header"><mat-icon>security</mat-icon> Security Group</div>
                            <div class="entity-body">
                                <p>Bundles features &amp; permissions. Attached to hats.</p>
                                <div class="entity-fields">
                                    <span class="field"><strong>key</strong>: string</span>
                                    <span class="field"><strong>name</strong>: string</span>
                                    <span class="field"><strong>features</strong>: string[]</span>
                                    <span class="field rel"
                                        ><strong>featureDetails</strong> &rarr; Feature + Permission[]</span
                                    >
                                </div>
                            </div>
                        </div>

                        <div class="entity-card feature-card">
                            <div class="entity-header"><mat-icon>toggle_on</mat-icon> Feature</div>
                            <div class="entity-body">
                                <p>A capability (e.g., organization, workspace, statistics).</p>
                                <div class="entity-fields">
                                    <span class="field"><strong>key</strong>: string</span>
                                    <span class="field"><strong>name</strong>: string</span>
                                    <span class="field"><strong>enabled</strong>: boolean</span>
                                    <span class="field"><strong>permissions</strong>: string[]</span>
                                    <span class="field"><strong>systemFeature</strong>: boolean</span>
                                </div>
                            </div>
                        </div>

                        <div class="entity-card policy-card">
                            <div class="entity-header"><mat-icon>policy</mat-icon> Policy</div>
                            <div class="entity-body">
                                <p>A document hat-wearers must acknowledge.</p>
                                <div class="entity-fields">
                                    <span class="field"><strong>key</strong>: string</span>
                                    <span class="field"><strong>name</strong>: string</span>
                                    <span class="field"><strong>status</strong>: draft&hellip;published</span>
                                    <span class="field"><strong>requiresAcknowledgment</strong></span>
                                    <span class="field"><strong>acknowledgmentDeadline</strong></span>
                                </div>
                            </div>
                        </div>

                        <div class="entity-card user-card">
                            <div class="entity-header"><mat-icon>person</mat-icon> User</div>
                            <div class="entity-body">
                                <p>A person in the organization. Wears hats to gain access.</p>
                                <div class="entity-fields">
                                    <span class="field"><strong>uid</strong>: string</span>
                                    <span class="field"><strong>name</strong>: string</span>
                                    <span class="field rel"><strong>hats</strong> &rarr; Hat[]</span>
                                    <span class="field computed"><strong>features</strong>: aggregated</span>
                                    <span class="field computed"><strong>featureDetails</strong>: aggregated</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Relationship table -->
                    <div class="relationship-section">
                        <h3><mat-icon>link</mat-icon> Relationships</h3>
                        <table class="rel-table">
                            <thead>
                                <tr>
                                    <th>From</th>
                                    <th></th>
                                    <th>To</th>
                                    <th>Via</th>
                                    <th>Cardinality</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Organization</td>
                                    <td>&rarr;</td>
                                    <td>Position</td>
                                    <td>orgParts[]</td>
                                    <td>1 : N</td>
                                </tr>
                                <tr>
                                    <td>Position</td>
                                    <td>&rarr;</td>
                                    <td>Position</td>
                                    <td>parentPartKey</td>
                                    <td>1 : N (tree)</td>
                                </tr>
                                <tr>
                                    <td>Hat</td>
                                    <td>&harr;</td>
                                    <td>Position</td>
                                    <td>positionArray[]</td>
                                    <td>M : N</td>
                                </tr>
                                <tr>
                                    <td>Hat</td>
                                    <td>&harr;</td>
                                    <td>User</td>
                                    <td>assignedToArray[]</td>
                                    <td>M : N</td>
                                </tr>
                                <tr>
                                    <td>Hat</td>
                                    <td>&harr;</td>
                                    <td>Security Group</td>
                                    <td>securityGroupsArray[]</td>
                                    <td>M : N</td>
                                </tr>
                                <tr>
                                    <td>Hat</td>
                                    <td>&harr;</td>
                                    <td>Policy</td>
                                    <td>policyKeys (map)</td>
                                    <td>M : N</td>
                                </tr>
                                <tr>
                                    <td>Security Group</td>
                                    <td>&rarr;</td>
                                    <td>Feature</td>
                                    <td>featureDetails (map)</td>
                                    <td>M : N</td>
                                </tr>
                                <tr>
                                    <td>Feature</td>
                                    <td>&rarr;</td>
                                    <td>Permission</td>
                                    <td>permissions[]</td>
                                    <td>1 : N</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Access chain -->
                    <div class="chain-section">
                        <h3><mat-icon>route</mat-icon> Access Resolution Chain</h3>
                        <div class="chain">
                            <span class="chain-node user-node">User</span>
                            <span class="chain-arrow">&rarr; wears &rarr;</span>
                            <span class="chain-node hat-node">Hat(s)</span>
                            <span class="chain-arrow">&rarr; has &rarr;</span>
                            <span class="chain-node sg-node">Security Group(s)</span>
                            <span class="chain-arrow">&rarr; grants &rarr;</span>
                            <span class="chain-node feat-node">Feature(s) + Permission(s)</span>
                            <span class="chain-arrow">&rarr; drives &rarr;</span>
                            <span class="chain-node ui-node">Menu / Routes / Actions</span>
                        </div>
                        <p class="chain-note">
                            Multiple hats = UNION of all features. Removing a hat triggers automatic recalculation.
                        </p>
                    </div>

                    <!-- Mermaid ER diagram -->
                    <div class="ontology-diagram-section">
                        <h3><mat-icon>schema</mat-icon> Entity-Relationship Diagram</h3>
                        <div class="zoom-toolbar" *ngIf="ontologyDiagramRendered">
                            <button mat-icon-button (click)="ontologyZoomIn()" matTooltip="Zoom in">
                                <mat-icon>zoom_in</mat-icon>
                            </button>
                            <button mat-icon-button (click)="ontologyZoomOut()" matTooltip="Zoom out">
                                <mat-icon>zoom_out</mat-icon>
                            </button>
                            <button mat-icon-button (click)="ontologyFitToWidth()" matTooltip="Fit to width">
                                <mat-icon>fit_screen</mat-icon>
                            </button>
                            <button mat-icon-button (click)="ontologyResetZoom()" matTooltip="Reset (100%)">
                                <mat-icon>center_focus_strong</mat-icon>
                            </button>
                            <span class="zoom-label">{{ Math.round(ontologyZoomLevel * 100) }}%</span>
                        </div>
                        <div #ontologyScrollArea class="diagram-scroll-area ontology-scroll">
                            <div
                                #ontologyMermaidContainer
                                class="mermaid-container"
                                [style.transform]="'scale(' + ontologyZoomLevel + ')'"
                                [style.transformOrigin]="'top left'"
                            ></div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Diagram Tab -->
            <div class="content diagram-content" [class.tab-hidden]="activeTab !== 'diagram'">
                <!-- Zoom toolbar -->
                <div class="zoom-toolbar" *ngIf="diagramRendered">
                    <button mat-icon-button (click)="zoomIn()" matTooltip="Zoom in">
                        <mat-icon>zoom_in</mat-icon>
                    </button>
                    <button mat-icon-button (click)="zoomOut()" matTooltip="Zoom out">
                        <mat-icon>zoom_out</mat-icon>
                    </button>
                    <button mat-icon-button (click)="fitToWidth()" matTooltip="Fit to width">
                        <mat-icon>fit_screen</mat-icon>
                    </button>
                    <button mat-icon-button (click)="resetZoom()" matTooltip="Reset (100%)">
                        <mat-icon>center_focus_strong</mat-icon>
                    </button>
                    <span class="zoom-label">{{ Math.round(zoomLevel * 100) }}%</span>
                </div>

                <div #diagramScrollArea class="diagram-scroll-area">
                    <div
                        #mermaidContainer
                        class="mermaid-container"
                        [style.transform]="'scale(' + zoomLevel + ')'"
                        [style.transformOrigin]="'top left'"
                    ></div>
                </div>
            </div>
        </div>
    `,
    styles: [
        `
            .dialog-container {
                display: flex;
                flex-direction: column;
                width: 100%;
                height: 100%;
                overflow: hidden;
            }

            /* Tab visibility */
            .tab-hidden {
                display: none !important;
            }

            .dialog-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 16px 24px;
                border-bottom: 1px solid rgba(0, 0, 0, 0.1);
                flex-shrink: 0;
            }
            .header-left {
                display: flex;
                align-items: center;
                gap: 12px;
                h2 {
                    margin: 0;
                    font-size: 18px;
                    font-weight: 500;
                }
            }
            .header-icon {
                color: #e65100;
            }
            .header-actions {
                display: flex;
                gap: 4px;
                button.active {
                    color: #e65100;
                    background: rgba(230, 81, 0, 0.08);
                }
            }

            .content {
                flex: 1;
                overflow-y: auto;
                padding: 24px;

                &::-webkit-scrollbar {
                    width: 6px;
                }
                &::-webkit-scrollbar-track {
                    background: transparent;
                }
                &::-webkit-scrollbar-thumb {
                    background: rgba(0, 0, 0, 0.15);
                    border-radius: 3px;
                }
            }

            /* --- Explanation tab: the docs guide in a frame --- */
            .explanation-content {
                padding: 0;
                overflow: hidden;
                display: flex;
            }
            .guide-frame {
                flex: 1;
                width: 100%;
                border: 0;
                background: #fff;
            }

            /* --- Diagram tab --- */
            .diagram-content {
                padding: 0;
                display: flex;
                flex-direction: column;
                overflow: hidden;
            }

            .zoom-toolbar {
                display: flex;
                align-items: center;
                gap: 2px;
                padding: 4px 16px;
                border-bottom: 1px solid rgba(0, 0, 0, 0.08);
                flex-shrink: 0;
                background: rgba(0, 0, 0, 0.02);

                .zoom-label {
                    margin-left: 8px;
                    font-size: 12px;
                    color: rgba(0, 0, 0, 0.5);
                    min-width: 40px;
                }
            }

            .diagram-scroll-area {
                flex: 1;
                overflow: auto;
                padding: 16px;

                &::-webkit-scrollbar {
                    width: 6px;
                    height: 6px;
                }
                &::-webkit-scrollbar-track {
                    background: transparent;
                }
                &::-webkit-scrollbar-thumb {
                    background: rgba(0, 0, 0, 0.15);
                    border-radius: 3px;
                }
            }

            .mermaid-container {
                display: inline-block;
                transform-origin: top left;
            }

            /* --- Ontology tab --- */
            .ontology-content {
                overflow-y: auto;
            }
            .ontology-sections {
                max-width: 900px;
                margin: 0 auto;
            }

            .ontology-intro {
                margin-bottom: 20px;
                h3 {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 16px;
                    font-weight: 600;
                    margin: 0 0 8px 0;
                    mat-icon {
                        color: #e65100;
                        font-size: 22px;
                        width: 22px;
                        height: 22px;
                    }
                }
                p {
                    font-size: 13px;
                    color: rgba(0, 0, 0, 0.7);
                    line-height: 1.6;
                    margin: 0;
                }
            }

            .entity-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
                gap: 12px;
                margin-bottom: 24px;
            }

            .entity-card {
                border-radius: 10px;
                border: 1px solid rgba(0, 0, 0, 0.1);
                overflow: hidden;
                .entity-header {
                    padding: 8px 12px;
                    font-weight: 600;
                    font-size: 13px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    color: #fff;
                    mat-icon {
                        font-size: 18px;
                        width: 18px;
                        height: 18px;
                    }
                }
                .entity-body {
                    padding: 10px 12px;
                    p {
                        font-size: 12px;
                        color: rgba(0, 0, 0, 0.65);
                        margin: 0 0 8px 0;
                        line-height: 1.5;
                    }
                }
            }
            .entity-fields {
                display: flex;
                flex-direction: column;
                gap: 2px;
                .field {
                    font-size: 11px;
                    font-family: 'Roboto Mono', monospace;
                    color: rgba(0, 0, 0, 0.7);
                    padding: 2px 4px;
                    border-radius: 3px;
                    &.rel {
                        background: rgba(230, 81, 0, 0.06);
                    }
                    &.computed {
                        background: rgba(25, 118, 210, 0.06);
                        font-style: italic;
                    }
                }
            }

            .org-card .entity-header {
                background: #1a237e;
            }
            .position-card .entity-header {
                background: #e65100;
            }
            .hat-card {
                border-color: #2e7d32;
                border-width: 2px;
            }
            .hat-card .entity-header {
                background: #2e7d32;
            }
            .sg-card .entity-header {
                background: #6a1b9a;
            }
            .feature-card .entity-header {
                background: #0d47a1;
            }
            .policy-card .entity-header {
                background: #00695c;
            }
            .user-card .entity-header {
                background: #c62828;
            }

            .relationship-section,
            .chain-section,
            .ontology-diagram-section {
                margin-bottom: 24px;
                h3 {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 14px;
                    font-weight: 600;
                    margin: 0 0 12px 0;
                    mat-icon {
                        color: #e65100;
                        font-size: 20px;
                        width: 20px;
                        height: 20px;
                    }
                }
            }

            .rel-table {
                width: 100%;
                border-collapse: collapse;
                font-size: 12px;
                th {
                    text-align: left;
                    padding: 6px 8px;
                    background: rgba(0, 0, 0, 0.04);
                    font-weight: 600;
                    border-bottom: 2px solid rgba(0, 0, 0, 0.1);
                }
                td {
                    padding: 5px 8px;
                    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
                    color: rgba(0, 0, 0, 0.75);
                }
                tr:hover td {
                    background: rgba(0, 0, 0, 0.02);
                }
            }

            .chain {
                display: flex;
                align-items: center;
                gap: 6px;
                flex-wrap: wrap;
                padding: 12px;
                border-radius: 10px;
                background: rgba(0, 0, 0, 0.02);
                border: 1px solid rgba(0, 0, 0, 0.06);
            }
            .chain-node {
                padding: 6px 12px;
                border-radius: 6px;
                font-size: 12px;
                font-weight: 600;
                color: #fff;
            }
            .chain-arrow {
                font-size: 11px;
                color: rgba(0, 0, 0, 0.4);
            }
            .user-node {
                background: #c62828;
            }
            .hat-node {
                background: #2e7d32;
            }
            .sg-node {
                background: #6a1b9a;
            }
            .feat-node {
                background: #0d47a1;
            }
            .ui-node {
                background: #37474f;
            }
            .chain-note {
                font-size: 11px;
                color: rgba(0, 0, 0, 0.5);
                margin-top: 8px;
                font-style: italic;
            }

            .ontology-scroll {
                max-height: 400px;
            }

            /* Dark theme */
            :host-context(.dark-theme) {
                .dialog-container {
                    background: #1e1e1e;
                    color: rgba(255, 255, 255, 0.87);
                }
                .dialog-header {
                    border-color: rgba(255, 255, 255, 0.1);
                }
                .header-icon {
                    color: #ffab40;
                }
                .header-actions button.active {
                    color: #ffab40;
                    background: rgba(255, 171, 64, 0.1);
                }
                .zoom-toolbar {
                    background: rgba(255, 255, 255, 0.03);
                    border-color: rgba(255, 255, 255, 0.08);
                    .zoom-label {
                        color: rgba(255, 255, 255, 0.4);
                    }
                }
                .content::-webkit-scrollbar-thumb,
                .diagram-scroll-area::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.15);
                }
                .ontology-intro p {
                    color: rgba(255, 255, 255, 0.7);
                }
                .entity-card {
                    border-color: rgba(255, 255, 255, 0.1);
                }
                .entity-card .entity-body p {
                    color: rgba(255, 255, 255, 0.6);
                }
                .entity-fields .field {
                    color: rgba(255, 255, 255, 0.7);
                    &.rel {
                        background: rgba(255, 171, 64, 0.1);
                    }
                    &.computed {
                        background: rgba(100, 181, 246, 0.1);
                    }
                }
                .rel-table th {
                    background: rgba(255, 255, 255, 0.05);
                    border-color: rgba(255, 255, 255, 0.1);
                    color: rgba(255, 255, 255, 0.87);
                }
                .rel-table td {
                    border-color: rgba(255, 255, 255, 0.06);
                    color: rgba(255, 255, 255, 0.7);
                }
                .rel-table tr:hover td {
                    background: rgba(255, 255, 255, 0.03);
                }
                .chain {
                    background: rgba(255, 255, 255, 0.03);
                    border-color: rgba(255, 255, 255, 0.06);
                }
                .chain-arrow {
                    color: rgba(255, 255, 255, 0.3);
                }
                .chain-note {
                    color: rgba(255, 255, 255, 0.4);
                }
            }
        `,
    ],
})
export class OrgStructureFlowDialogComponent {
    readonly guideUrl: SafeResourceUrl;

    @ViewChild('mermaidContainer') mermaidContainer!: ElementRef<HTMLDivElement>;
    @ViewChild('diagramScrollArea') diagramScrollArea!: ElementRef<HTMLDivElement>;
    @ViewChild('ontologyMermaidContainer') ontologyMermaidContainer!: ElementRef<HTMLDivElement>;
    @ViewChild('ontologyScrollArea') ontologyScrollArea!: ElementRef<HTMLDivElement>;

    activeTab: 'explanation' | 'diagram' | 'ontology' = 'explanation';
    zoomLevel = 1;
    ontologyZoomLevel = 1;
    Math = Math;

    diagramRendered = false;
    ontologyDiagramRendered = false;
    private renderCounter = 0;
    private svgNaturalWidth = 0;
    private ontologySvgNaturalWidth = 0;

    private readonly diagramMarkup = `
flowchart TB
    subgraph ORG ["ORGANIZATION"]
        direction TB
        O1["Organization Document"]
    end

    subgraph TREE ["ORG HIERARCHY (Parts Tree)"]
        direction TB
        T1["Executive (Level 0)"] --> T2["Division (Level 1)"]
        T2 --> T3["Department (Level 2)"]
        T3 --> T4["Section (Level 3)"]
        T4 --> T5["Unit (Level 4)"]
        T5 --> T6["Post (Level 5)"]
    end

    subgraph HATS ["HATS (Role Definitions)"]
        direction TB
        H1["Hat = A named role"] --> H2["Placed on position(s)"]
        H1 --> H3["Has Security Groups"]
        H1 --> H4["Has Policies"]
        H1 --> H5["Can have multiple users"]
    end

    subgraph ASSIGN ["USER ASSIGNMENT"]
        direction TB
        A1["Admin assigns user to hat"] --> A2["Security task created"]
        A2 --> A3["System collects all hats for user"]
        A3 --> A4["Aggregates features from all security groups"]
        A4 --> A5["Updates user profile with combined access"]
    end

    subgraph RESULT ["WHAT THE USER GETS"]
        direction TB
        R1["Menu items visible"] --> R2["Pages accessible"]
        R2 --> R3["Actions enabled per permissions"]
        R3 --> R4["Policies to acknowledge"]
    end

    subgraph MULTI ["MULTIPLE HATS"]
        direction TB
        M1["Hat A: HR Features"] --> M3["Combined: HR + Finance"]
        M2["Hat B: Finance Features"] --> M3
    end

    ORG --> TREE
    TREE -->|"positions have"| HATS
    HATS -->|"user wears hat"| ASSIGN
    ASSIGN --> RESULT
    ASSIGN -.->|"user can wear many"| MULTI
    MULTI --> RESULT

    style ORG fill:#1a237e,color:#fff,stroke:#3949ab
    style TREE fill:#e65100,color:#fff,stroke:#f57c00
    style HATS fill:#2e7d32,color:#fff,stroke:#43a047
    style ASSIGN fill:#6a1b9a,color:#fff,stroke:#8e24aa
    style RESULT fill:#0d47a1,color:#fff,stroke:#1976d2
    style MULTI fill:#00695c,color:#fff,stroke:#00897b
    `;

    private readonly ontologyMarkup = `
flowchart TB
    subgraph ORG ["ORGANIZATION"]
        O1["<b>Organization</b><br/><i>key, name, settings</i>"]
    end

    subgraph TREE ["POSITIONS (OrgPart)"]
        T1["<b>Position</b><br/><i>partKey, name, partType</i><br/><i>depth, path, parentPartKey</i>"]
        T1 -->|"parentPartKey"| T1
    end

    subgraph ROLES ["HATS (Central Hub)"]
        H1["<b>Hat</b><br/><i>key, name, description</i>"]
    end

    subgraph SEC ["SECURITY GROUPS"]
        S1["<b>Security Group</b><br/><i>key, name, features[]</i><br/><i>featureDetails{}</i>"]
    end

    subgraph FEAT ["FEATURES"]
        F1["<b>Feature</b><br/><i>key, name, enabled</i><br/><i>permissions[], systemFeature</i>"]
    end

    subgraph PERM ["PERMISSIONS"]
        P1["<b>Permission</b><br/><i>edit-org-board, manage-users,</i><br/><i>send-particles, view-stats...</i>"]
    end

    subgraph POL ["POLICIES"]
        PO1["<b>Policy</b><br/><i>key, name, status,</i><br/><i>requiresAcknowledgment</i>"]
    end

    subgraph USR ["USERS"]
        U1["<b>User</b><br/><i>uid, name, hats[]</i><br/><i>features[] (aggregated)</i><br/><i>featureDetails{} (aggregated)</i>"]
    end

    O1 -->|"1:N orgParts[]"| T1
    O1 -->|"1:N orgHats{}"| H1
    O1 -->|"1:N orgSecurityGroups{}"| S1
    O1 -->|"1:N orgFeatures{}"| F1
    O1 -->|"1:N orgPolicies{}"| PO1
    O1 -->|"1:N orgUsers{}"| U1

    H1 <-->|"M:N positionArray[]"| T1
    H1 <-->|"M:N assignedToArray[]"| U1
    H1 <-->|"M:N securityGroupsArray[]"| S1
    H1 <-->|"M:N policyKeys{}"| PO1

    S1 -->|"M:N featureDetails{}"| F1
    F1 -->|"1:N permissions[]"| P1

    style ORG fill:#1a237e,color:#fff,stroke:#3949ab
    style TREE fill:#e65100,color:#fff,stroke:#f57c00
    style ROLES fill:#2e7d32,color:#fff,stroke:#43a047
    style SEC fill:#6a1b9a,color:#fff,stroke:#8e24aa
    style FEAT fill:#0d47a1,color:#fff,stroke:#1976d2
    style PERM fill:#5c6bc0,color:#fff,stroke:#7986cb
    style POL fill:#00695c,color:#fff,stroke:#00897b
    style USR fill:#c62828,color:#fff,stroke:#e53935
    `;

    constructor(
        private dialogRef: MatDialogRef<OrgStructureFlowDialogComponent>,
        private cdr: ChangeDetectorRef,
        sanitizer: DomSanitizer
    ) {
        this.guideUrl = sanitizer.bypassSecurityTrustResourceUrl('assets/guides/ORG-STRUCTURE-GUIDE.html');
    }

    private async renderDiagram(): Promise<void> {
        if (this.diagramRendered) return;

        await new Promise((resolve) => setTimeout(resolve, 150));

        if (!this.mermaidContainer) return;

        this.diagramRendered = true;
        try {
            this.renderCounter++;
            const id = 'org-structure-' + this.renderCounter + '-' + Math.random().toString(36).slice(2, 6);

            mermaid.initialize({
                startOnLoad: false,
                theme: 'default',
                flowchart: {
                    useMaxWidth: false,
                    htmlLabels: true,
                    curve: 'basis',
                },
                securityLevel: 'loose',
            });

            const { svg } = await mermaid.render(id, this.diagramMarkup);
            this.mermaidContainer.nativeElement.innerHTML = svg;

            const svgEl = this.mermaidContainer.nativeElement.querySelector('svg');
            if (svgEl) {
                this.svgNaturalWidth = svgEl.getBoundingClientRect().width || svgEl.clientWidth || 800;
            }

            this.cdr.detectChanges();

            await new Promise((resolve) => setTimeout(resolve, 50));
            this.fitToWidth();
            this.cdr.detectChanges();
        } catch (err) {
            console.error('Failed to render Mermaid diagram:', err);
            this.mermaidContainer.nativeElement.innerHTML =
                '<p style="color: red; padding: 24px;">Failed to render diagram. Check console for details.</p>';
        }
    }

    switchToDiagram(): void {
        this.activeTab = 'diagram';
        if (!this.diagramRendered) {
            this.cdr.detectChanges();
            this.renderDiagram();
        }
    }

    switchToOntology(): void {
        this.activeTab = 'ontology';
        if (!this.ontologyDiagramRendered) {
            this.cdr.detectChanges();
            this.renderOntologyDiagram();
        }
    }

    private async renderOntologyDiagram(): Promise<void> {
        if (this.ontologyDiagramRendered) return;
        await new Promise((resolve) => setTimeout(resolve, 150));
        if (!this.ontologyMermaidContainer) return;

        this.ontologyDiagramRendered = true;
        try {
            this.renderCounter++;
            const id = 'ontology-' + this.renderCounter + '-' + Math.random().toString(36).slice(2, 6);

            mermaid.initialize({
                startOnLoad: false,
                theme: 'default',
                flowchart: { useMaxWidth: false, htmlLabels: true, curve: 'basis' },
                securityLevel: 'loose',
            });

            const { svg } = await mermaid.render(id, this.ontologyMarkup);
            this.ontologyMermaidContainer.nativeElement.innerHTML = svg;

            const svgEl = this.ontologyMermaidContainer.nativeElement.querySelector('svg');
            if (svgEl) {
                this.ontologySvgNaturalWidth = svgEl.getBoundingClientRect().width || svgEl.clientWidth || 800;
            }

            this.cdr.detectChanges();
            await new Promise((resolve) => setTimeout(resolve, 50));
            this.ontologyFitToWidth();
            this.cdr.detectChanges();
        } catch (err) {
            console.error('Failed to render ontology diagram:', err);
            this.ontologyMermaidContainer.nativeElement.innerHTML =
                '<p style="color: red; padding: 24px;">Failed to render ontology diagram. Check console for details.</p>';
        }
    }

    zoomIn(): void {
        this.zoomLevel = Math.min(3, this.zoomLevel + 0.15);
    }

    zoomOut(): void {
        this.zoomLevel = Math.max(0.2, this.zoomLevel - 0.15);
    }

    fitToWidth(): void {
        if (!this.diagramScrollArea || !this.svgNaturalWidth) return;
        const containerWidth = this.diagramScrollArea.nativeElement.clientWidth - 32;
        this.zoomLevel = containerWidth / this.svgNaturalWidth;
    }

    resetZoom(): void {
        this.zoomLevel = 1;
    }

    ontologyZoomIn(): void {
        this.ontologyZoomLevel = Math.min(3, this.ontologyZoomLevel + 0.15);
    }

    ontologyZoomOut(): void {
        this.ontologyZoomLevel = Math.max(0.2, this.ontologyZoomLevel - 0.15);
    }

    ontologyFitToWidth(): void {
        if (!this.ontologyScrollArea || !this.ontologySvgNaturalWidth) return;
        const containerWidth = this.ontologyScrollArea.nativeElement.clientWidth - 32;
        this.ontologyZoomLevel = containerWidth / this.ontologySvgNaturalWidth;
    }

    ontologyResetZoom(): void {
        this.ontologyZoomLevel = 1;
    }

    close(): void {
        this.dialogRef.close();
    }
}
